"use client";

import { OpenExchangeRates } from "@/services/forex/open-exchange-rates-service";
import React from "react";

const ExchangeRatesContext = React.createContext<{
  rates: OpenExchangeRates;
  convert: (amount: number, to: string, from?: string) => number;
  getCurrency: (code: string) => OpenExchangeRates["string"];
} | null>(null);

interface ExchangeRatesProviderProps {
  readonly children: React.ReactNode;
  readonly rates: OpenExchangeRates;
}

export function ExchangeRatesProvider({
  children,
  rates,
}: ExchangeRatesProviderProps) {
  const convert = (amount: number, to: string, from = "USD") => {
    const toRate = rates[to].rate;
    const fromRate = rates[from].rate;
    return (amount / fromRate) * toRate;
  };

  const getCurrency = (code: string) => rates[code];

  return (
    <ExchangeRatesContext.Provider value={{ rates, convert, getCurrency }}>
      {children}
    </ExchangeRatesContext.Provider>
  );
}

export function useExchangeRates() {
  const context = React.useContext(ExchangeRatesContext);
  if (context === null) {
    throw new Error(
      "useExchangeRates must be used within an ExchangeRatesProvider",
    );
  }
  return context;
}

export function useCurrencyExchangeRateInfo(code: string) {
  const { rates } = useExchangeRates();
  return (
    rates[code] ?? {
      code,
      rate: 1,
      name: "Unknown",
    }
  );
}
